import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import { createRule, deleteRule, listRules, updateRule } from "@/api/endpoints";
import { CommaListInput } from "@/components/CommaListInput";
import { InfoTip, type InfoTipContent } from "@/components/InfoTip";
import { GROUP_LABEL, GROUPS } from "@/lib/contaminantGroups";
import { TIPS } from "@/lib/glossary";
import type { ContaminantRule, RuleWrite, RuleWriteProfile } from "@/types/api";
import { usePageTitle } from "@/hooks/usePageTitle";

const LIMIT_TYPES = ["MCL", "MRDL", "ACTION_LEVEL", "SMCL", "TREATMENT_TECHNIQUE"];
const BASES = ["", "SINGLE", "RAA", "LRAA", "PERCENT_SAMPLES", "DOSE"];
const INTERVALS = [
  "ANNUAL",
  "QUARTERLY",
  "MONTHLY",
  "DAILY",
  "EVERY_N_YEARS",
  "BIANNUAL",
  "PER_COMPLIANCE_PERIOD",
];
const ALIGNMENTS = ["CALENDAR_YEAR", "COMPLIANCE_PERIOD", "ROLLING"];
const LOCATIONS = ["", "POE", "DISTRIBUTION", "TAPS", "PER_WELL", "PER_PLANT", "APPROVED_PLAN"];
const SOURCES = ["", "GW", "SW", "SUBPART_H", "GWUDI"];

const INTERVAL_LABEL: Record<string, string> = {
  ANNUAL: "Annual",
  QUARTERLY: "Quarterly",
  MONTHLY: "Monthly",
  DAILY: "Daily",
  EVERY_N_YEARS: "Every N yrs",
  BIANNUAL: "Biannual",
  PER_COMPLIANCE_PERIOD: "1 / period",
};

function appliesSummary(r: ContaminantRule): string {
  const sources = r.profiles.map((p) => p.applies_source).filter((s): s is string => !!s);
  const classes = r.profiles.flatMap((p) => p.applies_classes ?? []);
  const parts = [...new Set([...sources, ...classes])];
  return parts.length ? parts.join(" / ") : "All systems";
}

function freqSummary(r: ContaminantRule): string {
  const ivs = [...new Set(r.profiles.map((p) => INTERVAL_LABEL[p.interval_unit] ?? p.interval_unit))];
  return ivs.length ? ivs.join(" / ") : "—";
}

function effRange(r: ContaminantRule): string {
  const from = r.effective_from.slice(0, 4);
  return r.effective_to ? `${from}–${r.effective_to.slice(0, 4)}` : `${from}–`;
}

function limitText(r: ContaminantRule): string {
  if (r.limit_display) return r.limit_display;
  if (r.limit_value) return `${r.limit_type} ${r.limit_value}${r.limit_unit ? ` ${r.limit_unit}` : ""}`;
  return r.limit_type;
}

function emptyRule(): RuleWrite {
  return {
    contaminant_name: "",
    contaminant_group: "INORGANIC",
    limit_type: "MCL",
    limit_value: null,
    limit_unit: null,
    limit_display: null,
    compliance_basis: null,
    detection_limit: null,
    match_doc_type: null,
    subject_codes: null,
    reference_text: null,
    increased_monitoring_text: null,
    violation_text: null,
    response_actions_text: null,
    authority_cite: null,
    jurisdiction: "federal",
    needs_review: false,
    effective_from: new Date().toISOString().slice(0, 10),
    effective_to: null,
    profiles: [],
  };
}

function ruleToWrite(r: ContaminantRule): RuleWrite {
  return {
    contaminant_name: r.contaminant_name,
    contaminant_group: r.contaminant_group,
    limit_type: r.limit_type,
    limit_value: r.limit_value,
    limit_unit: r.limit_unit,
    limit_display: r.limit_display,
    compliance_basis: r.compliance_basis,
    detection_limit: r.detection_limit,
    match_doc_type: r.match_doc_type,
    subject_codes: r.subject_codes,
    reference_text: r.reference_text,
    increased_monitoring_text: r.increased_monitoring_text,
    violation_text: r.violation_text,
    response_actions_text: r.response_actions_text,
    authority_cite: r.authority_cite,
    jurisdiction: r.jurisdiction,
    needs_review: r.needs_review,
    effective_from: r.effective_from,
    effective_to: r.effective_to,
    profiles: r.profiles.map((p) => ({
      applies_classes: p.applies_classes,
      applies_source: p.applies_source,
      applies_pop_min: p.applies_pop_min,
      applies_pop_max: p.applies_pop_max,
      applies_treatment: p.applies_treatment,
      applies_susceptibility: p.applies_susceptibility,
      interval_unit: p.interval_unit,
      interval_n: p.interval_n,
      alignment: p.alignment,
      block_length_years: p.block_length_years,
      anchor_start_year: p.anchor_start_year,
      window_type: p.window_type,
      window_month: p.window_month,
      window_quarter: p.window_quarter,
      window_start: p.window_start,
      window_end: p.window_end,
      location: p.location,
      sample_count_fixed: p.sample_count_fixed,
      sample_count_by_population: p.sample_count_by_population,
    })),
  };
}

function emptyProfile(): RuleWriteProfile {
  return {
    applies_classes: null,
    applies_source: null,
    applies_pop_min: null,
    applies_pop_max: null,
    applies_treatment: null,
    applies_susceptibility: null,
    interval_unit: "ANNUAL",
    interval_n: null,
    alignment: "CALENDAR_YEAR",
    block_length_years: null,
    anchor_start_year: null,
    window_type: null,
    window_month: null,
    window_quarter: null,
    window_start: null,
    window_end: null,
    location: null,
    sample_count_fixed: null,
    sample_count_by_population: false,
  };
}

export function RuleLibraryAdmin(): JSX.Element {
  usePageTitle("Rule Library");
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const [reviewOnly, setReviewOnly] = useState(false);
  const [editing, setEditing] = useState<RuleWrite | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const query = useQuery({ queryKey: ["rules"], queryFn: listRules });

  const filtered = useMemo(() => {
    const rules = query.data ?? [];
    const q = search.trim().toLowerCase();
    return rules.filter((r) => {
      if (groupFilter !== "all" && r.contaminant_group !== groupFilter) return false;
      if (reviewOnly && !r.needs_review) return false;
      if (q && !r.contaminant_name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [query.data, search, groupFilter, reviewOnly]);

  const byGroup = useMemo(() => {
    const map = new Map<string, ContaminantRule[]>();
    for (const r of filtered) {
      const list = map.get(r.contaminant_group) ?? [];
      list.push(r);
      map.set(r.contaminant_group, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) =>
        a.contaminant_name === b.contaminant_name
          ? a.effective_from.localeCompare(b.effective_from)
          : a.contaminant_name.localeCompare(b.contaminant_name),
      );
    }
    return map;
  }, [filtered]);

  const reviewCount = (query.data ?? []).filter((r) => r.needs_review).length;

  function openNew(): void {
    setEditing(emptyRule());
    setEditingId(null);
  }

  function openEdit(r: ContaminantRule): void {
    setEditing(ruleToWrite(r));
    setEditingId(r.id);
  }

  function closeDrawer(): void {
    setEditing(null);
    setEditingId(null);
  }

  async function onSaved(): Promise<void> {
    await qc.invalidateQueries({ queryKey: ["rules"] });
    closeDrawer();
  }

  if (query.isLoading) {
    return <div className="skeleton h-64" aria-label="Loading rule library" />;
  }
  if (query.error) {
    const msg = query.error instanceof ApiError ? query.error.message : "Failed to load";
    return <p className="text-sm text-severity-high">{msg}</p>;
  }

  const total = query.data?.length ?? 0;

  return (
    <div className="space-y-6">
      <header className="flex items-baseline justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Regulatory Rule Library</h1>
          <p className="text-sm text-ink-300">
            {total} rule{total === 1 ? "" : "s"}
            {reviewCount > 0 && <> · {reviewCount} need review</>}
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={openNew}>
          + New rule
        </button>
      </header>

      {reviewCount > 0 && (
        <div
          className="card border-l-4 border-severity-medium bg-severity-medium/10"
          role="status"
        >
          <p className="text-sm text-ink-100">
            <strong>{reviewCount}</strong> rule{reviewCount === 1 ? "" : "s"} marked{" "}
            <span className="pill-medium">⚠ needs review</span> — their values haven&apos;t
            been verified against a primary source yet, so they are{" "}
            <strong>not auto-evaluated</strong> in monitoring schedules or compliance
            views. Review each one, verify against eCFR/EPA/FDEP, and untick
            &ldquo;Needs review&rdquo; to activate it.
          </p>
          <button
            type="button"
            className="text-sm text-ink-300 hover:text-ink-100 underline mt-1"
            onClick={() => setReviewOnly(true)}
          >
            Show only rules needing review
          </button>
        </div>
      )}

      <section className="card flex flex-wrap items-end gap-3">
        <label className="block space-y-1">
          <span className="text-sm text-ink-200">Search</span>
          <input
            className="input"
            placeholder="Contaminant name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm text-ink-200 inline-flex items-center">
            Group
            <InfoTip {...TIPS.ruleGroup} />
          </span>
          <select className="input" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
            <option value="all">All groups</option>
            {GROUPS.map((g) => (
              <option key={g} value={g}>
                {GROUP_LABEL[g]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-ink-200">
          <input type="checkbox" checked={reviewOnly} onChange={(e) => setReviewOnly(e.target.checked)} />
          Needs review only
          <InfoTip {...TIPS.needsReview} />
        </label>
      </section>

      {GROUPS.filter((g) => byGroup.has(g)).map((g) => (
        <section key={g} className="card overflow-x-auto">
          <h2 className="card-title">
            {GROUP_LABEL[g]} <span className="text-ink-400 font-normal">({byGroup.get(g)?.length})</span>
          </h2>
          <table className="w-full text-sm">
            <thead className="text-left text-ink-300 border-b border-ink-700">
              <tr>
                <th className="py-2 pr-4 font-medium">Contaminant</th>
                <th className="py-2 pr-4 font-medium">Applies to</th>
                <th className="py-2 pr-4 font-medium">Limit</th>
                <th className="py-2 pr-4 font-medium">Frequency</th>
                <th className="py-2 pr-4 font-medium">Effective</th>
                <th className="py-2 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-700">
              {byGroup.get(g)?.map((r) => (
                <tr key={r.id}>
                  <td className="py-2 pr-4">
                    {r.contaminant_name}
                    {r.jurisdiction === "florida" && (
                      <span
                        className="ml-2 text-[10px] uppercase tracking-wide text-ink-400 ring-1 ring-ink-600 rounded px-1 py-0.5"
                        title="Florida-specific requirement (FDEP), not a federal EPA baseline"
                      >
                        FL
                      </span>
                    )}
                    {r.needs_review && (
                      <span className="pill-medium ml-2" title="Needs review">
                        ⚠
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-ink-300">{appliesSummary(r)}</td>
                  <td className="py-2 pr-4">{limitText(r)}</td>
                  <td className="py-2 pr-4 text-ink-300">{freqSummary(r)}</td>
                  <td className="py-2 pr-4 text-ink-300 font-mono text-xs">{effRange(r)}</td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      className="btn-primary py-1 px-2 text-xs"
                      onClick={() => openEdit(r)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}

      {filtered.length === 0 && (
        <p className="text-sm text-ink-300">No rules match the current filters.</p>
      )}

      {editing && (
        <RuleDrawer
          draft={editing}
          ruleId={editingId}
          onChange={setEditing}
          onClose={closeDrawer}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

interface RuleDrawerProps {
  draft: RuleWrite;
  ruleId: string | null;
  onChange: (next: RuleWrite) => void;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

let nextProfileKey = 1;

function RuleDrawer({ draft, ruleId, onChange, onClose, onSaved }: RuleDrawerProps): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const dirty = useRef(false);
  // Stable identities for removable profile rows (index keys teleport
  // focus/state when a middle row is deleted).
  const profileKeys = useRef<number[]>(draft.profiles.map(() => nextProfileKey++));
  if (profileKeys.current.length < draft.profiles.length) {
    while (profileKeys.current.length < draft.profiles.length) {
      profileKeys.current.push(nextProfileKey++);
    }
  }
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Dialog behavior: Escape closes (with a dirty check), body scroll locks,
  // and initial focus lands inside the panel. Backdrop click gets the same
  // dirty check — previously it silently discarded unsaved edits.
  function requestClose(): void {
    // eslint-disable-next-line no-alert
    if (!dirty.current || window.confirm("Discard unsaved changes to this rule?")) {
      onClose();
    }
  }
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") requestCloseRef.current();
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const save = useMutation({
    mutationFn: () => (ruleId ? updateRule(ruleId, draft) : createRule(draft)),
    onSuccess: () => void onSaved(),
    onError: (e) => setError(e instanceof ApiError ? e.message : "Save failed"),
  });

  const remove = useMutation({
    mutationFn: () => deleteRule(ruleId as string),
    onSuccess: () => void onSaved(),
    onError: (e) => setError(e instanceof ApiError ? e.message : "Delete failed"),
  });

  function set<K extends keyof RuleWrite>(key: K, value: RuleWrite[K]): void {
    dirty.current = true;
    onChange({ ...draft, [key]: value });
  }

  function str(value: string): string | null {
    return value.trim() === "" ? null : value;
  }

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    if (!draft.contaminant_name.trim()) {
      setError("Contaminant name is required.");
      return;
    }
    save.mutate();
  }

  function setProfile(i: number, next: RuleWriteProfile): void {
    dirty.current = true;
    const profiles = draft.profiles.map((p, idx) => (idx === i ? next : p));
    onChange({ ...draft, profiles });
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={requestClose}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ruleId ? "Edit rule" : "New rule"}
        tabIndex={-1}
        className="h-full w-full max-w-2xl overflow-y-auto bg-ink-900 ring-1 ring-ink-700 p-6 space-y-4 focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">{ruleId ? "Edit rule" : "New rule"}</h2>
          <button type="button" className="text-ink-300 text-sm" onClick={requestClose}>
            Close
          </button>
        </header>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contaminant">
              <input
                className="input"
                value={draft.contaminant_name}
                onChange={(e) => set("contaminant_name", e.target.value)}
              />
            </Field>
            <Field label="Group" tip={TIPS.ruleGroup}>
              <select
                className="input"
                value={draft.contaminant_group}
                onChange={(e) => set("contaminant_group", e.target.value)}
              >
                {GROUPS.map((g) => (
                  <option key={g} value={g}>
                    {GROUP_LABEL[g]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Limit type" tip={TIPS.limitType}>
              <select
                className="input"
                value={draft.limit_type}
                onChange={(e) => set("limit_type", e.target.value)}
              >
                {LIMIT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Compliance basis" tip={TIPS.complianceBasis}>
              <select
                className="input"
                value={draft.compliance_basis ?? ""}
                onChange={(e) => set("compliance_basis", str(e.target.value))}
              >
                {BASES.map((b) => (
                  <option key={b} value={b}>
                    {b || "—"}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Limit value" tip={TIPS.limitValue}>
              <input
                className="input"
                value={draft.limit_value ?? ""}
                onChange={(e) => set("limit_value", str(e.target.value))}
              />
            </Field>
            <Field label="Unit" tip={TIPS.limitUnit}>
              <input
                className="input"
                value={draft.limit_unit ?? ""}
                onChange={(e) => set("limit_unit", str(e.target.value))}
              />
            </Field>
            <Field label="Limit display (ranges / qualitative)" tip={TIPS.limitDisplay}>
              <input
                className="input"
                placeholder="e.g. 6.5–8.5, Non-corrosive"
                value={draft.limit_display ?? ""}
                onChange={(e) => set("limit_display", str(e.target.value))}
              />
            </Field>
            <Field label="Detection limit (RDL)" tip={TIPS.detectionLimit}>
              <input
                className="input"
                value={draft.detection_limit ?? ""}
                onChange={(e) => set("detection_limit", str(e.target.value))}
              />
            </Field>
            <Field label="Match doc type" tip={TIPS.matchDocType}>
              <input
                className="input"
                value={draft.match_doc_type ?? ""}
                onChange={(e) => set("match_doc_type", str(e.target.value))}
              />
            </Field>
            <Field label="Subject codes (comma-sep)" tip={TIPS.subjectCodes}>
              <CommaListInput
                value={draft.subject_codes ?? null}
                onChange={(codes) => set("subject_codes", codes)}
              />
            </Field>
            <Field label="Effective from" tip={TIPS.effectiveFrom}>
              <input
                type="date"
                className="input"
                value={draft.effective_from}
                onChange={(e) => set("effective_from", e.target.value)}
              />
            </Field>
            <Field label="Effective to" tip={TIPS.effectiveTo}>
              <input
                type="date"
                className="input"
                value={draft.effective_to ?? ""}
                onChange={(e) => set("effective_to", str(e.target.value))}
              />
            </Field>
            <Field label="Authority cite" tip={TIPS.authorityCite}>
              <input
                className="input"
                value={draft.authority_cite ?? ""}
                onChange={(e) => set("authority_cite", str(e.target.value))}
              />
            </Field>
            <Field label="Jurisdiction" tip={{ label: "Jurisdiction", body: "Federal = EPA baseline requirement. Florida = FDEP-specific or more stringent state requirement (e.g. Florida bacteriological monitoring, secondary standards)." }}>
              <select
                className="input"
                value={draft.jurisdiction}
                onChange={(e) => set("jurisdiction", e.target.value as RuleWrite["jurisdiction"])}
              >
                <option value="federal">Federal (EPA)</option>
                <option value="florida">Florida (FDEP)</option>
              </select>
            </Field>
            <label className="flex items-end gap-2 text-sm text-ink-200 pb-2">
              <input
                type="checkbox"
                checked={draft.needs_review}
                onChange={(e) => set("needs_review", e.target.checked)}
              />
              Needs review
              <InfoTip {...TIPS.needsReview} />
            </label>
          </div>

          <TextArea label="Reference" tip={TIPS.referenceText} value={draft.reference_text} onChange={(v) => set("reference_text", v)} />
          <TextArea
            label="Increased monitoring / trigger"
            tip={TIPS.increasedMonitoring}
            value={draft.increased_monitoring_text}
            onChange={(v) => set("increased_monitoring_text", v)}
          />
          <TextArea
            label="Violation determination"
            tip={TIPS.violationText}
            value={draft.violation_text}
            onChange={(v) => set("violation_text", v)}
          />
          <TextArea
            label="Required response actions"
            tip={TIPS.responseActions}
            value={draft.response_actions_text}
            onChange={(v) => set("response_actions_text", v)}
          />

          <section className="space-y-2">
            <div className="flex items-baseline justify-between">
              <h3 className="card-title mb-0 inline-flex items-center">
                Monitoring profiles
                <InfoTip {...TIPS.monitoringProfiles} />
              </h3>
              <button
                type="button"
                className="text-xs text-ink-300 underline"
                onClick={() => {
                  dirty.current = true;
                  profileKeys.current.push(nextProfileKey++);
                  onChange({ ...draft, profiles: [...draft.profiles, emptyProfile()] });
                }}
              >
                + add profile
              </button>
            </div>
            {draft.profiles.length === 0 && (
              <p className="text-xs text-ink-400">No profiles — add at least one cadence.</p>
            )}
            {draft.profiles.map((p, i) => (
              <ProfileEditor
                key={profileKeys.current[i] ?? i}
                profile={p}
                onChange={(next) => setProfile(i, next)}
                onRemove={() => {
                  dirty.current = true;
                  profileKeys.current.splice(i, 1);
                  onChange({ ...draft, profiles: draft.profiles.filter((_, idx) => idx !== i) });
                }}
              />
            ))}
          </section>

          {error && <p className="text-sm text-severity-high">{error}</p>}

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" className="btn-primary" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </button>
            <button type="button" className="text-sm text-ink-300" onClick={requestClose}>
              Cancel
            </button>
            {ruleId && (
              <button
                type="button"
                className="ml-auto text-sm text-severity-high"
                disabled={remove.isPending}
                onClick={() => {
                  if (confirm("Delete this rule version?")) remove.mutate(); // eslint-disable-line no-alert
                }}
              >
                {remove.isPending ? "Deleting…" : "Delete"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

interface ProfileEditorProps {
  profile: RuleWriteProfile;
  onChange: (next: RuleWriteProfile) => void;
  onRemove: () => void;
}

function ProfileEditor({ profile, onChange, onRemove }: ProfileEditorProps): JSX.Element {
  function set<K extends keyof RuleWriteProfile>(key: K, value: RuleWriteProfile[K]): void {
    onChange({ ...profile, [key]: value });
  }
  function num(value: string): number | null {
    if (value.trim() === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null; // never ship NaN to the API
  }
  function s(value: string): string | null {
    return value.trim() === "" ? null : value;
  }

  return (
    <div className="rounded-md ring-1 ring-ink-700 p-3 grid grid-cols-3 gap-2 text-sm">
      <Field label="Applies source" tip={TIPS.appliesSource}>
        <select className="input" value={profile.applies_source ?? ""} onChange={(e) => set("applies_source", s(e.target.value))}>
          {SOURCES.map((src) => (
            <option key={src} value={src}>
              {src || "any"}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Interval" tip={TIPS.interval}>
        <select className="input" value={profile.interval_unit} onChange={(e) => set("interval_unit", e.target.value)}>
          {INTERVALS.map((iv) => (
            <option key={iv} value={iv}>
              {INTERVAL_LABEL[iv] ?? iv}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Alignment" tip={TIPS.alignment}>
        <select className="input" value={profile.alignment} onChange={(e) => set("alignment", e.target.value)}>
          {ALIGNMENTS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Interval N" tip={TIPS.intervalN}>
        <input className="input" value={profile.interval_n ?? ""} onChange={(e) => set("interval_n", num(e.target.value))} />
      </Field>
      <Field label="Block yrs" tip={TIPS.blockYears}>
        <input className="input" value={profile.block_length_years ?? ""} onChange={(e) => set("block_length_years", num(e.target.value))} />
      </Field>
      <Field label="Anchor yr" tip={TIPS.anchorYear}>
        <input className="input" value={profile.anchor_start_year ?? ""} onChange={(e) => set("anchor_start_year", num(e.target.value))} />
      </Field>
      <Field label="Location" tip={TIPS.location}>
        <select className="input" value={profile.location ?? ""} onChange={(e) => set("location", s(e.target.value))}>
          {LOCATIONS.map((loc) => (
            <option key={loc} value={loc}>
              {loc || "—"}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Window month" tip={TIPS.windowMonth}>
        <input className="input" value={profile.window_month ?? ""} onChange={(e) => set("window_month", num(e.target.value))} />
      </Field>
      <Field label="Pop min / max" tip={TIPS.popRange}>
        <div className="flex gap-1">
          <input className="input" value={profile.applies_pop_min ?? ""} onChange={(e) => set("applies_pop_min", num(e.target.value))} />
          <input className="input" value={profile.applies_pop_max ?? ""} onChange={(e) => set("applies_pop_max", num(e.target.value))} />
        </div>
      </Field>
      <div className="col-span-3 flex justify-end">
        <button type="button" className="text-xs text-severity-high" onClick={onRemove}>
          Remove profile
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  tip,
  children,
}: {
  label: string;
  tip?: InfoTipContent;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-ink-300 inline-flex items-center">
        {label}
        {tip && <InfoTip {...tip} />}
      </span>
      {children}
    </label>
  );
}

function TextArea({
  label,
  tip,
  value,
  onChange,
}: {
  label: string;
  tip?: InfoTipContent;
  value: string | null;
  onChange: (v: string | null) => void;
}): JSX.Element {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-ink-300 inline-flex items-center">
        {label}
        {tip && <InfoTip {...tip} />}
      </span>
      <textarea
        className="input min-h-[60px]"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value.trim() === "" ? null : e.target.value)}
      />
    </label>
  );
}
